import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DailySalesSummaryPrintPopup from './DailySalesSummaryPrintPopup';
import { useDispatch } from 'react-redux';
import { reportHandler, branchHandler } from '../redux/handlers';
import moment from 'moment';

export function DailySalesSummary() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { businessSlug, date } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const branchPosId = queryParams.get('branchPosId');
  const branchId = queryParams.get('branchId');

  const [isPrintPopupOpen, setIsPrintPopupOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchData, setBranchData] = useState(null);
  const [businessData, setBusinessData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        console.log('branchId : ', branchId);

        if (branchId) {
          const branchResult = await dispatch(branchHandler.getBranchById(branchId));
          if (branchResult) {
            setBranchData(branchResult);
            if (branchResult.business) {
              setBusinessData(branchResult.business);
            }
          }
        }

        let formattedDate = date;
        if (date && date.includes('-')) {
          const [day, month, year] = date.split('-');
          formattedDate = `${year}-${month}-${day}`;
        }

        const data = await dispatch(
          reportHandler.getSingleSalesReport(businessSlug, branchId, branchPosId, formattedDate)
        );
        if (data) {
          setReportData(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, businessSlug, branchPosId, branchId, date]);

  const formatNumber = num => {
    return (num || 0).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handlePrint = () => {
    setIsPrintPopupOpen(true);
  };

  const handlePrintComplete = () => {
    setIsPrintPopupOpen(false);
  };

  const formatDate = dateString => {
    if (!dateString) return '';
    return moment(dateString).format('DD/MM/YYYY');
  };

  const formatTime = dateString => {
    if (!dateString) return '';
    return moment(dateString).format('HH:mm');
  };

  const findPaymentMethodByCode = code => {
    if (!reportData || !reportData.system_balance) return { amount: 0, payment_providers: [] };
    return (
      reportData.system_balance.find(method => method.payment_method_code === code) || {
        amount: 0,
        payment_providers: []
      }
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">กำลังโหลดข้อมูล...</div>;
  }

  if (!reportData) {
    return <div className="flex justify-center items-center min-h-screen">ไม่พบข้อมูลรายงาน</div>;
  }

  const cashPayment = findPaymentMethodByCode('CASH');
  const bankTransferPayment = findPaymentMethodByCode('BANK_TRANSFER');
  const creditCardPayment = findPaymentMethodByCode('CREDIT_CARD');
  const deliveryPayment = findPaymentMethodByCode('DELIVERY');

  const reportDate = formatDate(reportData.start_date_time);
  const currentDate = formatDate(new Date());
  const printTime = formatTime(new Date());

  const branchName = branchData ? branchData.branch_name : '';
  const branchCode = branchData ? branchData.code : '';

  const printData = {
    date: date,
    currentDate: currentDate,
    printTime: printTime,
    company: {
      name: businessData ? businessData.company_name : '',
      branch: branchName,
      branchCode: branchCode,
      taxId: businessData ? businessData.tax_number : '',
      vatIncluded: businessData?.setting_is_vat || false
    },
    tickets: {
      sold: reportData.total_bill_sale_count || 0,
      canceled: reportData.total_bill_cancel_count || 0,
      total_bill_void_count: reportData.total_bill_void_count || 0,
      void: 0,
      refund: reportData.total_bill_return_count || 0,
      taxInvoice: reportData.total_full_tax_invoice_count || 0,
      taxInvoiceValue: reportData.total_full_tax_invoice_amount || 0
    },
    sales: {
      total: reportData.sales || 0,
      saleRefund: reportData.sale_refund || 0,
      afterRefund: reportData.after_refund || 0,
      itemDiscount: reportData.item_discount || 0,
      subDiscount: reportData.sub_discount || 0,
      pointDiscount: 0,
      couponDiscount: 0,
      serviceFee: reportData.service_charge || 0,
      totalSales: reportData.total_sales || 0,
      creditCardFee: reportData.credit_card_fee || 0,
      vatOnFee: reportData.vat_on_fee || 0,
      nonTaxableAmount: reportData.non_taxable_amount || 0,
      taxableAmount: (reportData.total_sale_amount || 0) + (reportData.credit_card_fee || 0),
      vat: (reportData.total_vat_amount || 0) + (reportData.vat_on_fee || 0),
      totalFinalAmount: reportData.total_final_amount || reportData.total_payment_loss_rounded || 0,
      totalPaymentAmount: reportData.total_payment_amount || reportData.total_payment_loss_rounded || 0,
      netAmountAdj: (reportData.total_payment_amount || 0) + (reportData.loss_rounded || 0)
    },
    payments: {
      bankTransfer: {
        total: bankTransferPayment.amount,
        details: bankTransferPayment.payment_providers.map(provider => ({
          name: provider.payment_provider_name,
          amount: provider.amount
        }))
      },
      cash: cashPayment.amount,
      creditCard: creditCardPayment.amount,
      delivery: {
        total: deliveryPayment.amount,
        details: deliveryPayment.payment_providers.map(provider => ({
          name: provider.payment_provider_name,
          amount: provider.amount
        }))
      },
      cashTotal: reportData.cash_total || reportData.cash_float || 0,
      lossRounded: reportData.loss_rounded || 0,
      summaryDiff: reportData.summary?.diff || reportData.loss_rounded || 0
    }
  };

  return (
    <div className="flex flex-col justify-center items-center text-black p-16">
      <div className="w-full max-w-md bg-white p-4 flex flex-col">
        <div className="text-center mb-4">
          <div className="text-lg font-semibold">สรุปยอดขายรวมสิ้นวัน ณ วันที่: {date}</div>
          <div className="text-sm">
            วันที่พิมพ์: {currentDate} {printTime} น
          </div>
          <div className="font-medium">
            {printData.company.name} - {printData.company.branch} ({printData.company.branchCode})
          </div>
          {printData.company.taxId && (
            <div>
              TAX ID: {printData.company.taxId}{' '}
              {businessData?.setting_vat_calculation_id
                ? businessData?.settingVatCalculation?.code === 'INCLUDED'
                  ? '(VAT Included)'
                  : businessData?.settingVatCalculation?.code === 'SEPARATE'
                  ? '(VAT Excluded)'
                  : ''
                : businessData?.setting_is_vat
                ? '(VAT Included)'
                : ''}
            </div>
          )}
        </div>

        <div className="border-dashed border-t border-black my-2"></div>

        <div className="flex flex-col gap-1 mb-2">
          <div className="flex justify-between">
            <div>จำนวนบิลขาย</div>
            <div>{printData.tickets.sold}</div>
          </div>
          <div className="flex justify-between">
            <div>จำนวนบิลที่ยกเลิก</div>
            <div>{printData.tickets.canceled}</div>
          </div>
          <div className="flex justify-between">
            <div>จำนวนบิล Void</div>
            <div>{printData.tickets.total_bill_void_count}</div>
          </div>
          <div className="flex justify-between">
            <div>จำนวนบิลที่คืนสินค้า</div>
            <div>{printData.tickets.refund}</div>
          </div>
          <div className="flex justify-between">
            <div>จำนวนบิล ออกใบกำกับภาษีเต็มรูปแบบ</div>
            <div>{printData.tickets.taxInvoice}</div>
          </div>
          <div className="flex justify-between">
            <div>มูลค่า ออกใบกำกับภาษี เต็มรูปแบบ</div>
            <div>{formatNumber(printData.tickets.taxInvoiceValue)}</div>
          </div>
        </div>

        <div className="border-dashed border-t border-black my-2"></div>

        {reportData.initial_cash !== undefined && (
          <div className="flex justify-between">
            <div>เงินสดตั้งต้น</div>
            <div>{formatNumber(reportData.initial_cash)}</div>
          </div>
        )}
        <div className="flex justify-between">
          <div>ยอดขาย</div>
          <div>{formatNumber(printData.sales.total)}</div>
        </div>
        <div className="flex justify-between">
          <div>-Sale Refund</div>
          <div>{formatNumber(printData.sales.saleRefund)}</div>
        </div>
        <div className="flex justify-between">
          <div>-Affer Refund</div>
          <div>{formatNumber(printData.sales.afterRefund)}</div>
        </div>
        <div className="flex justify-between">
          <div>-Item Discount</div>
          <div>{formatNumber(printData.sales.itemDiscount)}</div>
        </div>
        <div className="flex justify-between">
          <div>-Sub Discount</div>
          <div>{formatNumber(printData.sales.subDiscount)}</div>
        </div>
        {reportData.total_sales !== undefined && (
          <div className="flex justify-between">
            <div>ยอดรวมขาย</div>
            <div>{formatNumber(reportData.total_sales)}</div>
          </div>
        )}
        <div className="flex justify-between">
          <div>ค่าธรรมเนียม</div>
          <div>{formatNumber(printData.sales.serviceFee)}</div>
        </div>
        <div className="flex justify-between">
          <div className="whitespace-nowrap font-bold">ยอดสุทธิ</div>
          <div className="text-right font-bold">{formatNumber(printData.sales.totalFinalAmount)}</div>
        </div>

        <div className="border-dashed border-t border-black my-2"></div>

        {reportData.credit_card_fee !== undefined && (
          <>
            <div className="flex justify-between">
              <div>ค่าธรรมเนียมบัตรเครดิต</div>
              <div>{formatNumber(reportData.credit_card_fee)}</div>
            </div>
            <div className="flex justify-between">
              <div>ภาษีมูลค่าเพิ่ม</div>
              <div>{formatNumber(reportData.vat_on_fee)}</div>
            </div>
            <div className="border-dashed border-t border-black my-2"></div>
          </>
        )}

        <div className="flex justify-between">
          <div>มูลค่าสินค้าไม่มีภาษี</div>
          <div>{formatNumber(printData.sales.nonTaxableAmount)}</div>
        </div>
        <div className="flex justify-between">
          <div>มูลค่าสินค้ามีภาษี</div>
          <div>{formatNumber(printData.sales.taxableAmount)}</div>
        </div>
        <div className="flex justify-between">
          <div>ภาษีมูลค่าเพิ่ม</div>
          <div>{formatNumber(printData.sales.vat)}</div>
        </div>

        {reportData.total_payment_amount !== undefined && (
          <div className="flex flex-col justify-center px-16">
            <div className="flex justify-between w-full font-semibold">
              <div>Net Amount</div>
              <div>{formatNumber(printData.sales.totalPaymentAmount)}</div>
            </div>
            <div className="flex justify-between w-full font-semibold">
              <div>Net Amount Adj.</div>
              <div>{formatNumber(printData.sales.netAmountAdj)}</div>
            </div>
          </div>
        )}
        {reportData.total_payment_amount === undefined && (
          <div className="flex justify-between font-bold text-lg">
            <div>Net Amount</div>
            <div>{formatNumber(printData.sales.totalPaymentAmount)}</div>
          </div>
        )}

        <div className="border-dashed border-t border-black my-2"></div>

        <div className="flex justify-between">
          <div className="whitespace-nowrap">ช่องทางการชำระ</div>
        </div>
        {reportData.system_balance &&
          reportData.system_balance.map((paymentMethod, index) => (
            <div key={index}>
              <div className="flex justify-between">
                <div className="whitespace-nowrap">{paymentMethod.payment_method_name}</div>
                <div className="text-right">
                  {Array.isArray(paymentMethod.payment_providers) &&
                    paymentMethod.payment_providers.length === 0 &&
                    formatNumber(paymentMethod.amount)}
                </div>
              </div>

              {Array.isArray(paymentMethod.payment_providers) &&
                paymentMethod.payment_providers.map((provider, providerIndex) => (
                  <div className="flex justify-between" key={`provider-${providerIndex}`}>
                    <div className="whitespace-nowrap pl-4">- {provider.payment_provider_name}</div>
                    <div className="text-right">{formatNumber(provider.amount)}</div>
                  </div>
                ))}
            </div>
          ))}

        <div className="border-dashed border-t border-black my-2"></div>

        {reportData.loss_rounded !== undefined && (
          <div className="flex justify-between">
            <div>กำไร(ขาดทุน)ปัดเศษ</div>
            <div>{formatNumber(reportData.loss_rounded)}</div>
          </div>
        )}

        <div className="flex justify-between">
          <div>ยอดนับเงินสด</div>
          <div>{formatNumber(printData.payments.cashTotal)}</div>
        </div>

        <div className="flex justify-between font-semibold px-16">
          <div>Diff</div>
          <div>{formatNumber(printData.payments.summaryDiff)}</div>
        </div>

        <div className="border-dashed border-t border-black my-4"></div>
      </div>
      <div className="flex justify-between mt-4 mb-2 space-x-6">
        <button
          className="bg-red-600 text-white p-3 rounded-md font-medium text-center w-48"
          style={{ backgroundColor: '#FF0000' }}
          onClick={() => {
            navigate(`/${businessSlug}/report/sales`);
          }}
        >
          กลับสู่หน้ารายงาน
        </button>
        <button
          className="bg-green-500 text-white p-3 rounded-md font-medium text-center w-48"
          style={{ backgroundColor: '#2DC76D' }}
          onClick={handlePrint}
        >
          PRINT
        </button>
      </div>

      <DailySalesSummaryPrintPopup isOpen={isPrintPopupOpen} onPrintComplete={handlePrintComplete} data={printData} />
    </div>
  );
}
